import { useMemo } from "react";
import { ADMIN_ADDRESSES } from "@cede/utils";
import { useHubUser } from "./useHubUser";

/**
 * localStorage key for storing the debug override address
 * This allows admins to impersonate other users for testing purposes
 */
export const DEBUG_OVERRIDE_ADDRESS_KEY = "cede-override-address-claim-rewards";

/**
 * Unified hook to get the effective user address with debug override support.
 *
 * This hook enables admin addresses to override the user address for testing purposes.
 * The override address is stored in localStorage under the key "cede-override-address-claim-rewards".
 *
 * @returns The effective address (override if admin and set, otherwise real address)
 *
 * @example
 * ```ts
 * const address = useDebugOverrideAddress();
 * // Use address in queries
 * const { data } = useQuery({
 *   queryKey: ['data', address],
 *   queryFn: () => apiHelper.getWithAuth(auth, endpoint, { queryParams: { defiAddress: address } })
 * });
 * ```
 *
 * To set an override address (only works for admin addresses):
 * ```ts
 * // In browser console or dev tools
 * localStorage.setItem('cede-override-address-claim-rewards', '0x1234...');
 * ```
 *
 * @see {@link useClaimRewards} for usage in claim rewards flow
 * @see {@link useSpinningWheelHistory} for usage in spinning wheel history
 */
export const useDebugOverrideAddress = () => {
  const { hubUser } = useHubUser();

  /**
   * Check if the current user is an admin and if there's an override address set
   */
  const address = useMemo(() => {
    const realAddress = hubUser?.defiAddress;
    if (!realAddress) return undefined;

    // Only allow override for admin addresses
    if (ADMIN_ADDRESSES.includes(realAddress)) {
      const overrideAddress = localStorage.getItem(DEBUG_OVERRIDE_ADDRESS_KEY);
      if (overrideAddress) {
        return overrideAddress;
      }
    }

    return realAddress;
  }, [hubUser?.defiAddress]);

  return address;
};
